import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Input } from '@codes/ui-kit/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { Textarea } from '@codes/ui-kit/components/ui/textarea';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { PromptBody, PromptView } from '@lemoncloud/ssocio-chatbots-api';
import { useQueryClient } from '@tanstack/react-query';
import { updateAgentCachesForPromptUpdate } from '@web/app/features/agents/utils';
import { PromptRevisionList } from '@web/app/features/prompts/components/PromptRevisionList';
import { Loader2 } from 'lucide-react';

import { useCreatePrompt, usePrompt, useRevisePrompt } from '@codes/agents';

import { updateCacheWithNewPrompt, updateCacheWithUpdated, updateRevisionCacheWithNew } from '../utils';

interface PromptFormProps {
    mode: 'create' | 'edit';
    promptId: string | null;
    promptData?: PromptView | null;
    onFormSuccess: (promptId: string) => void;
    className?: string;
}

interface PromptFormData {
    name: string;
    stereo: 'system' | 'user';
    contents: string;
    isStock?: boolean;
}

export const PromptForm = ({ mode, promptData, promptId, onFormSuccess, className }: PromptFormProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const defaultType = searchParams.get('type') as 'system' | 'user' | null;

    const [formData, setFormData] = useState<PromptFormData>({
        name: '',
        stereo: mode === 'create' ? defaultType || 'system' : 'system',
        contents: '',
    });

    const { data: fallbackPrompt, isLoading: isLoadingPrompt } = usePrompt(promptId || '');

    const currentPrompt = promptData || fallbackPrompt || null;

    const createMutation = useCreatePrompt();
    const reviseMutation = useRevisePrompt();

    const arrayToTextarea = (contents: string[]): string => {
        return contents.join('\n');
    };

    const textareaToArray = (content: string): string[] => {
        return content.split('\n');
    };

    useEffect(() => {
        if (mode === 'edit' && currentPrompt) {
            setFormData({
                name: currentPrompt.name || '',
                stereo: (currentPrompt.stereo as 'system' | 'user') || 'system',
                contents: arrayToTextarea(currentPrompt.contents || ['']),
                // isStock: (currentPrompt as any).isStock || false,
            });
        } else if (mode === 'create') {
            setFormData({
                name: '',
                stereo: defaultType || 'system',
                contents: '',
                // isStock: false,
            });
        }
    }, [mode, currentPrompt, defaultType]);

    const handleFormDataChange = (updates: Partial<PromptFormData>) => {
        setFormData(prev => ({ ...prev, ...updates }));
    };

    const validateFormData = (formData: PromptFormData) => {
        if (!formData.name.trim()) {
            throw new Error(t('prompt.form.validation.nameRequired'));
        }
        if (!formData.contents.trim()) {
            throw new Error(t('prompt.form.validation.contentRequired'));
        }
    };

    const handleCreate = async (finalFormData: PromptBody) => {
        const result = await createMutation.mutateAsync(finalFormData);
        updateCacheWithNewPrompt(queryClient, 'prompts', result);
        return {
            id: result.id || '',
            message: t('prompt.form.messages.createSuccess'),
        };
    };

    const handleEdit = async (finalFormData: PromptBody, promptId: string) => {
        const result = await reviseMutation.mutateAsync({
            promptId,
            ...finalFormData,
        });

        const updatedPrompt: PromptView = result.$parent || null;
        if (!updatedPrompt) {
            throw new Error('Updated prompt not found in response');
        }

        updateCacheWithUpdated(queryClient, 'prompts', updatedPrompt, promptId);
        updateAgentCachesForPromptUpdate(queryClient, updatedPrompt, promptId);

        // revision이 생성되면 revision 캐시에도 추가
        if (result && result.$parent && promptId) {
            updateRevisionCacheWithNew(queryClient, promptId, result.$parent);
        }

        return {
            id: updatedPrompt.id || '',
            message: t('prompt.form.messages.updateSuccess'),
        };
    };

    const handleSave = async () => {
        try {
            validateFormData(formData);

            const finalFormData: PromptBody = {
                ...formData,
                contents: textareaToArray(formData.contents as string),
            };

            const result =
                mode === 'create' ? await handleCreate(finalFormData) : await handleEdit(finalFormData, promptId!);

            toast({ title: result.message });
            onFormSuccess(result.id);
        } catch (error) {
            const message = error instanceof Error ? error.message : t('prompt.form.messages.saveError');

            console.error('Error in handleSave:', error);
            toast({ title: message, variant: 'destructive' });
        }
    };

    const isSaving = createMutation.isPending || reviseMutation.isPending;
    const isPromptLoading = mode === 'edit' && Boolean(promptId) && !promptData && isLoadingPrompt;
    const isLoading = isSaving || isPromptLoading;

    const isFormValid = formData.name.trim() && formData.contents.trim();

    return (
        <div
            className={`w-full max-w-[992px] h-full bg-card shadow-custom border border-chatbot-line rounded-2xl max-md:h-full flex flex-col ${className}`}
        >
            {/* 헤더 */}
            <div className="flex items-center justify-between p-4">
                <div className="text-sm font-medium">
                    {mode === 'create' ? t('prompt.form.create.title') : t('prompt.form.edit.title')}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        className="w-[52px] h-8 text-xs"
                        onClick={handleSave}
                        disabled={!isFormValid || isLoading}
                    >
                        {isSaving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            t(mode === 'create' ? 'common.create' : 'common.save')
                        )}
                    </Button>
                </div>
            </div>

            {/* 폼 내용 */}
            <div className="flex-1 overflow-auto p-4 pt-0 space-y-4 max-md:space-y-4">
                {isPromptLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* 기본 정보 */}
                        <div className="space-y-6 max-md:space-y-4">
                            <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
                                <div>
                                    <label
                                        htmlFor="name"
                                        className="inline-block text-sm font-medium text-text mb-[9px]"
                                    >
                                        {t('prompt.form.basic.name')}
                                    </label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={e => handleFormDataChange({ name: e.target.value })}
                                        placeholder={t('prompt.form.basic.namePlaceholder')}
                                        className="h-[45px] border-placeholder"
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="stereo"
                                        className="inline-block text-sm font-medium text-text mb-[9px]"
                                    >
                                        {t('prompt.form.basic.stereo')}
                                    </label>
                                    <Select
                                        value={formData.stereo}
                                        onValueChange={(value: 'system' | 'user') =>
                                            handleFormDataChange({ stereo: value })
                                        }
                                    >
                                        <SelectTrigger className="h-[45px] border-placeholder">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="system">{t('prompt.stereo.system')}</SelectItem>
                                            <SelectItem value="user">{t('prompt.stereo.user')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            {/* <div className="flex gap-4"> */}
                            {/*TODO: add isStock form*/}
                            {/*<div className="flex-1">*/}
                            {/*    <label*/}
                            {/*        htmlFor="isStock"*/}
                            {/*        className="inline-block text-base font-medium text-text mb-[9px]"*/}
                            {/*    >*/}
                            {/*        {t('prompt.form.basic.isStock')}*/}
                            {/*    </label>*/}
                            {/*    <Select*/}
                            {/*        value={formData.isStock.toString()}*/}
                            {/*        onValueChange={(value: string) =>*/}
                            {/*            handleFormDataChange({ isStock: value === 'true' })*/}
                            {/*        }*/}
                            {/*    >*/}
                            {/*        <SelectTrigger className="h-[45px] border-[#84888F]">*/}
                            {/*            <SelectValue />*/}
                            {/*        </SelectTrigger>*/}
                            {/*        <SelectContent>*/}
                            {/*            <SelectItem value="true">True</SelectItem>*/}
                            {/*            <SelectItem value="false">False</SelectItem>*/}
                            {/*        </SelectContent>*/}
                            {/*    </Select>*/}
                            {/*</div>*/}
                            {/* </div> */}
                        </div>

                        {/* 내용 */}
                        <div>
                            <label className="block text-sm font-medium mb-2">{t('prompt.form.content.title')}</label>
                            <Textarea
                                value={formData.contents}
                                onChange={e => handleFormDataChange({ contents: e.target.value })}
                                placeholder={t('prompt.form.content.description')}
                                className="min-h-[300px]"
                                rows={15}
                            />
                        </div>

                        {mode === 'edit' && promptId && (
                            <PromptRevisionList
                                promptId={promptId}
                                currentPrompt={currentPrompt}
                                onSelectRevision={revision => {
                                    if (revision.contents) {
                                        const content = revision.contents.join('\n');
                                        handleFormDataChange({ contents: content });
                                    }
                                }}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
